import { useState, useEffect } from "react";
import { X, ThumbsUp, ThumbsDown, Send, Check } from "lucide-react";

interface FeedbackSheetProps {
  open: boolean;
  onClose: () => void;
  category: string;           // "corner" | "error" | "empty_state" | "milestone" | "nps"
  context?: string;           // e.g. "first_shift", "dashboard_empty", error message
  title?: string;             // custom title (defaults to "Share your feedback")
  prompt?: string;            // custom prompt text
  showSentiment?: boolean;    // show thumbs up/down (default true)
}

export default function FeedbackSheet({
  open,
  onClose,
  category,
  context,
  title = "Share your feedback",
  prompt = "What's on your mind? Any friction, bugs, or ideas — we want to hear.",
  showSentiment = true,
}: FeedbackSheetProps) {
  const [sentiment, setSentiment] = useState<"positive" | "negative" | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setSentiment(null);
      setComment("");
      setSubmitting(false);
      setSubmitted(false);
    }
  }, [open]);

  async function handleSubmit() {
    if (!comment.trim() && !sentiment) return;
    setSubmitting(true);
    try {
      await fetch("/api/feedback", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          context: context || null,
          sentiment,
          comment: comment.trim() || null,
          pageUrl: window.location.pathname,
        }),
      });
      setSubmitted(true);
      setTimeout(onClose, 1500);
    } catch {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        zIndex: 10000, display: "flex", alignItems: "flex-end", justifyContent: "center",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          padding: 24, maxWidth: 480, width: "100%",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.2)",
          animation: "slideUp 0.25s ease",
          fontFamily: "inherit",
        }}
      >
        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>

        {submitted ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: "var(--color-success-light)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <Check size={28} color="var(--color-success)" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text)" }}>
              Thanks — we got it.
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--color-text)", marginBottom: 4 }}>
                  {title}
                </div>
                <div style={{ fontSize: 13, color: "var(--color-text-muted)", lineHeight: 1.5 }}>
                  {prompt}
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: 4, color: "var(--color-text-muted)",
                }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {showSentiment && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <button
                  onClick={() => setSentiment("positive")}
                  style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    padding: "10px 14px", borderRadius: 10,
                    border: sentiment === "positive" ? "2px solid var(--color-success)" : "1px solid var(--color-border)",
                    background: sentiment === "positive" ? "var(--color-success-light)" : "var(--color-bg)",
                    color: sentiment === "positive" ? "var(--color-success)" : "var(--color-text-muted)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  <ThumbsUp size={14} /> Good
                </button>
                <button
                  onClick={() => setSentiment("negative")}
                  style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    padding: "10px 14px", borderRadius: 10,
                    border: sentiment === "negative" ? "2px solid var(--color-danger)" : "1px solid var(--color-border)",
                    background: sentiment === "negative" ? "var(--color-danger-light)" : "var(--color-bg)",
                    color: sentiment === "negative" ? "var(--color-danger)" : "var(--color-text-muted)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  <ThumbsDown size={14} /> Needs work
                </button>
              </div>
            )}

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional: tell us more..."
              rows={4}
              style={{
                width: "100%", padding: "10px 12px",
                border: "1px solid var(--color-border)", borderRadius: 10,
                background: "var(--color-bg)", color: "var(--color-text)",
                fontSize: 14, fontFamily: "inherit", resize: "vertical",
                marginBottom: 12,
              }}
            />

            <button
              onClick={handleSubmit}
              disabled={submitting || (!comment.trim() && !sentiment)}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 10, border: "none",
                background: "var(--color-primary)", color: "var(--color-on-primary)",
                fontSize: 14, fontWeight: 600, cursor: submitting ? "default" : "pointer",
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                opacity: (!comment.trim() && !sentiment) ? 0.5 : 1,
              }}
            >
              <Send size={14} />
              {submitting ? "Sending..." : "Send feedback"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
